import React from 'react';

// Shared inline-SVG visuals for the VPS Health module. No deps, no Tailwind —
// pure SVG + CSS variables so they theme with the rest of the app.

// Map a host/service status to a CSS color token.
export function statusColor(status) {
  switch (status) {
    case 'ok': return 'var(--c-resolved, #22c55e)';
    case 'warning': return 'var(--c-pending, #f59e0b)';
    case 'critical': return 'var(--c-danger, #ef4444)';
    case 'offline': return 'var(--c-danger, #ef4444)';
    default: return 'var(--c-text-muted, #94a3b8)';
  }
}

// Pick a color for a metric percentage against warn/crit thresholds.
export function pctColor(pct, warn = 80, crit = 92) {
  if (pct == null || isNaN(pct)) return 'var(--c-text-muted, #94a3b8)';
  if (pct >= crit) return 'var(--c-danger, #ef4444)';
  if (pct >= warn) return 'var(--c-pending, #f59e0b)';
  return 'var(--c-resolved, #22c55e)';
}

// Radial gauge: a ring with a labeled center value (percentage).
export function Gauge({ value, label, sub, size = 96, warn = 80, crit = 92 }) {
  const pct = value == null || isNaN(value) ? null : Math.max(0, Math.min(100, value));
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = pct == null ? c : c * (1 - pct / 100);
  const color = pctColor(pct, warn, crit);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--c-border)" strokeWidth={stroke} opacity={0.5} />
          {pct != null && (
            <circle
              cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
              strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
              style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease' }}
            />
          )}
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', lineHeight: 1.1,
        }}>
          <span style={{ fontSize: size * 0.24, fontWeight: 700, color: 'var(--c-text)' }}>
            {pct == null ? '—' : `${Math.round(pct)}%`}
          </span>
          {sub && <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>{sub}</span>}
        </div>
      </div>
      {label && <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 600 }}>{label}</span>}
    </div>
  );
}

// Sparkline / area chart from an array of numbers.
export function Sparkline({ data, width = 240, height = 48, color = 'var(--c-brand)', fill = true }) {
  const nums = (data || []).map(Number).filter((n) => !isNaN(n));
  if (nums.length < 2) {
    // Not enough samples yet — a faint dashed baseline reads cleaner than a bare dash.
    return (
      <svg width={width} height={height} style={{ display: 'block', width: '100%' }} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} stroke={color} strokeWidth={1.5} strokeDasharray="3 4" opacity={0.3} />
      </svg>
    );
  }
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const stepX = width / (nums.length - 1);
  const pts = nums.map((v, i) => {
    const x = i * stepX;
    const y = height - 4 - ((v - min) / span) * (height - 8);
    return [x, y];
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} style={{ display: 'block', width: '100%' }} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {fill && <path d={area} fill={color} opacity={0.12} />}
      <path d={line} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Small status dot.
export function StatusDot({ status, size = 10 }) {
  const color = statusColor(status);
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      background: color, boxShadow: `0 0 0 3px ${color}22`, flexShrink: 0,
    }} />
  );
}

// Humanize uptime seconds → "12d 3h" / "3h 20m" / "8m".
export function formatUptime(seconds) {
  const s = Number(seconds);
  if (!s || isNaN(s) || s < 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// "3.2 / 8 GB" from used/total MB.
export function formatMemMB(usedMb, totalMb) {
  if (usedMb == null || totalMb == null) return '—';
  const toGb = (mb) => (mb / 1024).toFixed(mb / 1024 >= 10 ? 0 : 1);
  return `${toGb(usedMb)} / ${toGb(totalMb)} GB`;
}

// bytes/sec → human throughput ("12.3 Mbps" / "850 Kbps").
export function formatBps(bytesPerSec) {
  const b = Number(bytesPerSec);
  if (!b || isNaN(b) || b < 0) return '0 bps';
  const bits = b * 8;
  if (bits >= 1e9) return `${(bits / 1e9).toFixed(1)} Gbps`;
  if (bits >= 1e6) return `${(bits / 1e6).toFixed(1)} Mbps`;
  if (bits >= 1e3) return `${(bits / 1e3).toFixed(0)} Kbps`;
  return `${Math.round(bits)} bps`;
}
